'use client';

import React, { useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTranslations } from 'next-intl';

/**
 * CopyLink – a compact, accessible copy-to-clipboard box.
 *
 * Usage:
 * <CopyLink url="https://www.wildtangent.com/wishlist.php?publicwishlist=ad252182-3aeb-437..." />
 *
 * Looks like the screenshot: dark canvas, white bordered box with the URL.
 */

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

export type CopyLinkProps = {
  /** URL to display & copy */
  url: string;
  /** Optional heading shown above the box (uppercase, subtle) */
  heading?: string;
  /**
   * Extra wrapper classes. Useful if you want to control max-width or margins
   * from the parent layout.
   */
  className?: string;
  /** If true, the entire box is clickable to copy (default: true) */
  boxClickable?: boolean;
};

export default function CopyLink({ url, heading, className, boxClickable = true }: CopyLinkProps) {
  const [copied, setCopied] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('Components.copyLink');

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const selection = window.getSelection();
      if (selection && textRef.current) {
        const range = document.createRange();
        range.selectNodeContents(textRef.current);
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
      }
    } finally {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  }

  return (
    <div className={cn('mx-auto w-full max-w-5xl px-6 pt-4 pb-1', className)}>
      {heading && (
        <div className='mb-4 text-center text-xs uppercase opacity-80 sm:text-sm'>{heading}</div>
      )}

      <div
        role={boxClickable ? 'button' : undefined}
        tabIndex={boxClickable ? 0 : -1}
        aria-label='Copy link'
        onClick={boxClickable ? copyToClipboard : undefined}
        onKeyDown={
          boxClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  copyToClipboard();
                }
              }
            : undefined
        }
        className={cn(
          'group relative flex items-center justify-between rounded-md border',
          'px-4 py-4 sm:px-6 sm:py-5',
          'outline-hidden focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900',
          boxClickable && 'cursor-pointer select-none',
        )}
      >
        <div ref={textRef} className='truncate text-base font-medium sm:text-lg'>
          {url}
        </div>

        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard();
          }}
          className={cn(
            'ml-3 inline-flex items-center gap-2 rounded-md border border-white/20 px-3 py-2',
            'text-sm font-medium opacity-90 hover:opacity-100',
            'focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-hidden',
          )}
          aria-live='polite'
        >
          {copied ? (
            <>
              <Check className='h-4 w-4' />
              {t('copied')}
            </>
          ) : (
            <>
              <Copy className='h-4 w-4' />
              {t('copy')}
            </>
          )}
        </button>
      </div>

      <div
        aria-live='polite'
        className={cn(
          'mt-2 text-center text-xs transition-opacity duration-200',
          copied ? 'opacity-100' : 'opacity-0',
        )}
      >
        {t('linkCopiedToClipboard')}
      </div>
    </div>
  );
}
